"use client";

import { CloseOutlined } from "@ant-design/icons";
import Button from "antd/lib/button";
import Modal from "antd/lib/modal";
import Row from "antd/lib/row";
import Space from "antd/lib/space";
import Text from "antd/lib/typography/Text";
import Title from "antd/lib/typography/Title";
import { useRef } from "react";
import { toast } from "react-hot-toast";
import { BsTwitter, BsWhatsapp } from "react-icons/bs";
import { FaFacebookF } from "react-icons/fa";
import { IoMdClose } from "react-icons/io";
import { IoCheckmarkDone } from "react-icons/io5";
import { MdContentCopy, MdRemoveDone } from "react-icons/md";
import {
  FacebookShareButton,
  TwitterShareButton,
  WhatsappShareButton,
} from "react-share";
import { State } from "../models/gameModels.ts";
import { difficulty } from "../styles/difficultyColors.ts";
import { sizes } from "../styles/sizes.ts";
import { colors } from "../styles/themes.tsx";
import { formatDateForPresentation } from "../utils/dt-format.ts";
import { SolutionIcon, SolutionItemBox } from "./SolutionItemBox.tsx";
import Spacer from "./Spacer.tsx";
import { StreakChips } from "./StreakChips.tsx";
import Link from "next/link";

const EventReportingButton = ({
  socialName,
  shouldShareTime,
  backgroundColor,
  color,
  ...props
}: {
  socialName: string;
  shouldShareTime: boolean;
  color: string;
  backgroundColor: string;
} & React.ComponentProps<typeof Button>) => {
  const onClick = () => {
    // reportShareEvent(socialName, shouldShareTime);
  };
  return (
    <Button
      type="text"
      onClick={onClick}
      style={{
        width: "40px",
        height: "40px",
        margin: "9px",
        padding: 0,
        backgroundColor: backgroundColor,
        color: color,
        cursor: "pointer",
        // borderColor: backgroundColor,
        // border: '1px solid'
      }}
      styles={{
        icon: {
          textAlign: "center",
          verticalAlign: "middle",
          lineHeight: "40px",
          fontSize: "26px",
          color: color,
        },
      }}
      {...props} // Assuming other props are compatible with Ant Design Button
    />
  );
};

const CompletedModalCustomGame = ({
  closeHandler,
  isOpen,
  finalState,
  puzzleUrl,
  author,
}: {
  puzzleUrl: string;
  puzzleName: string;
  author: string;
  isOpen: boolean;
  closeHandler: () => void;
  finalState: State;
}) => {
  const win = finalState.result === "win";
  const guesses = finalState.guesses;
  const shareRef = useRef(null);
  let shareMessage = win
    ? `פתרתי את חיבורים של ${author}. עכשיו תורך!`
    : `תוכלו לפתור את החיבורים של ${author}?`;
  shareMessage += `\nתאריך: ${formatDateForPresentation(new Date())}`;
  shareMessage += "\nהפתרון שלי:\n";
  const difficultyIcon = {
    1: "🟨",
    2: "🟩",
    3: "🟦",
    4: "🟪",
  };
  guesses.map((guess) => {
    shareMessage += guess.correct ? "✅" : guess.one_way_category ? "🔶" : "❌";
    shareMessage += guess.words
      .map((guess) => difficultyIcon[guess.difficulty])
      .join("");
    shareMessage += "\n";
  });

  return (
    <Modal
      cancelButtonProps={{ style: { display: "none" } }}
      closeIcon={<CloseOutlined style={{ zIndex: 200 }} />}
      open={isOpen}
      okText={"סגור"}
      onOk={closeHandler}
      onCancel={closeHandler}
      width="90%"
      zIndex={4}
      styles={{
        body: {
          animation: "popIn 0.2s ease forwards",
        },
        content: {
          border: "1px solid",
          margin: "auto",
          marginBottom: "32px",
          width: sizes.modalWidth,
        },
      }}
    >
      <div
        dir="rtl"
        style={{
          height: "100%",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <div
          ref={shareRef}
          dir="rtl"
          style={{
            height: "100%",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Space direction="vertical" align="center" style={{ width: "100%" }}>
            <Title
              level={2}
              style={{
                fontWeight: 600,
                textTransform: "uppercase",
                padding: 2,
              }}
              tabIndex={0}
            >
              {win ? "כל הכבוד 🥳" : "נסה שוב מחר 😔"}
            </Title>
            <Text style={{ fontSize: "large", fontWeight: 300 }} tabIndex={0}>
              {"הפתרון שלך:"}
            </Text>
          </Space>
          <Space direction="horizontal" align="center">
            <div tabIndex={0}>
              {guesses.map((guess, i) => (
                <div key={i} style={{ display: "flex" }}>
                  <div
                    aria-label={
                      guess.correct
                        ? "ניחוש נכון."
                        : guess.one_way_category
                          ? "כמעט."
                          : "טעות."
                    }
                    role={"img"}
                    tabIndex={0}
                  >
                    <SolutionIcon
                      icon={
                        guess.correct
                          ? IoCheckmarkDone
                          : guess.one_way_category
                            ? MdRemoveDone
                            : IoMdClose
                      }
                      color={
                        guess.correct
                          ? "green"
                          : guess.one_way_category
                            ? "#fcba03"
                            : "red"
                      }
                      aria-hidden={true}
                    />
                  </div>
                  {guess.words.map((word, j) => (
                    <SolutionItemBox
                      key={j}
                      color={difficulty[word.difficulty]}
                    />
                  ))}
                </div>
              ))}
            </div>
            <div
              className="dont-include-imageshare"
              style={{ width: 0, marginLeft: "auto" }}
            >
              <StreakChips />
            </div>
          </Space>
          <div
            hidden={true}
            className="include-imageshare"
            style={{ padding: "10px" }}
          ></div>
          <div>
            <Link href={`${document.location.origin}/custom`} passHref>
              צור פאזל משלך!
            </Link>
          </div>
        </div>
        <Space
          direction="vertical"
          align="start"
          style={{
            width: "100%",
            marginTop: "32px",
          }}
        >
          <Text
            className="dont-include-imageshare"
            style={{
              fontSize: "22px",
              fontWeight: 600,
              padding: "8px 0",
              lineHeight: "20px",
            }}
          >
            שתף עם חברים
          </Text>
          <Row
            className="dont-include-imageshare"
            gutter={[16, 16]}
            style={{ marginBottom: 8 }}
          >
            <FacebookShareButton url={puzzleUrl} tabIndex={-1}>
              <EventReportingButton
                socialName="facebook"
                shouldShareTime={false}
                aria-label={"Share on Facebook"}
                icon={<FaFacebookF />}
                color="white"
                backgroundColor="#0866ff"
                // borderRadius={"30%"}
                // paddingTop="4px"
                // padding={0}
              />
            </FacebookShareButton>
            <Spacer />
            <TwitterShareButton
              url={puzzleUrl}
              title={shareMessage}
              tabIndex={-1}
            >
              <EventReportingButton
                socialName="twitter"
                shouldShareTime={false}
                backgroundColor="#1DA1F2"
                color="white"
                aria-label="Share on Twitter"
                icon={<BsTwitter />}
              />
            </TwitterShareButton>
            <Spacer />
            <WhatsappShareButton
              url={puzzleUrl}
              title={shareMessage}
              tabIndex={-1}
            >
              <EventReportingButton
                socialName="whatsapp"
                shouldShareTime={false}
                aria-label="Share on Whatsapp"
                icon={<BsWhatsapp />}
                backgroundColor="#25D366"
                color="white"
              />
            </WhatsappShareButton>
            <Spacer />

            <EventReportingButton
              backgroundColor="rgba(0,0,0,0)"
              socialName="copy"
              color={colors.color}
              type="default"
              shouldShareTime={false}
              aria-label="Copy to clipboard"
              icon={<MdContentCopy />}
              onClick={async () => {
                await navigator.clipboard.writeText(shareMessage + puzzleUrl);
                toast("הועתק", {
                  duration: 1000,
                  position: "bottom-center",
                  id: "copy",
                  // Styling
                  style: {},
                  className: "",

                  // Change colors of success/error/loading icon
                  iconTheme: {
                    primary: "#fff",
                    secondary: "#000",
                  },

                  // Aria
                  ariaProps: {
                    role: "status",
                    "aria-live": "polite",
                  },
                });
              }}
            />
          </Row>
        </Space>
      </div>
    </Modal>
  );
};

export default CompletedModalCustomGame;
